import type {QuestionnaireQuestion} from 'src/types/ask-ai';


export type RawAnswer = string | string[] | undefined;

function isEmpty(answer: RawAnswer): boolean {
  if (answer == null) {
    return true;
  }
  if (typeof answer === 'string') {
    return answer.trim() === '';
  }
  return answer.length === 0;
}

/**
 * Validate a single question's answer at the question level (doc §5.5).
 * Returns an error message string, or null when valid.
 */
export function validateAnswer(
  question: QuestionnaireQuestion,
  answer: RawAnswer,
): string | null {
  const {validation, is_required} = question;

  if (!validation) {
    if (is_required && isEmpty(answer)) {
      return 'This field is required';
    }
    return null;
  }

  // Split on the FIRST colon only so regex patterns containing ':' survive.
  const idx = validation.rule.indexOf(':');
  const rule = idx === -1 ? validation.rule : validation.rule.slice(0, idx);
  const arg = idx === -1 ? '' : validation.rule.slice(idx + 1);
  const fail = validation.error_message;

  switch (rule) {
    case 'required':
      return isEmpty(answer) ? fail : null;
    case 'min_length':
      return typeof answer === 'string' && answer.length < Number(arg)
        ? fail
        : null;
    case 'max_length':
      return typeof answer === 'string' && answer.length > Number(arg)
        ? fail
        : null;
    case 'pattern':
      try {
        return typeof answer === 'string' && !new RegExp(arg).test(answer)
          ? fail
          : null;
      } catch {
        return null; // malformed regex from backend — do not block the user
      }
    case 'min_selections':
      return Array.isArray(answer) && answer.length < Number(arg) ? fail : null;
    case 'max_selections':
      return Array.isArray(answer) && answer.length > Number(arg) ? fail : null;
    default:
      return null;
  }
}
